/**
 * Shared report cache update utilities for WebSocket-driven and other in-place updates.
 *
 * Use these so that:
 * - All pre-existing caches stay in sync (app cache + React Query reportsInProcess).
 * - One canonical implementation: WebSocket lock/unlock handlers and 409 conflict
 *   handlers (e.g. when clicking into a locked report) should use the same path.
 *
 * Flow: update app cache → persist (optional) → update React Query caches → notify (optional).
 */

import type { QueryClient } from "react-query";
import type { ApplicationCache } from "../../api/Cache";
import type { ReportSummaryApi, ReportSummaryItemApi } from "../../api/Reports";
import type { LatestWorkflowStepRunApi } from "../../api/ReportsApi";

export type ReportUpdateFn = (
  report: ReportSummaryItemApi
) => ReportSummaryItemApi;

/**
 * React Query key prefixes for cached in-process misconduct report summary lists.
 * Legacy v2025 uses reportsInProcess; v2026 uses misconductListsV2026 with tab in_process.
 */
const IN_PROCESS_REPORT_LIST_QUERY_PREFIXES = [
  ["reportsInProcess"],
  ["misconductListsV2026", "in_process"],
] as const;

function findInProcessReportListQueries(queryClient: QueryClient) {
  const queryCache = queryClient.getQueryCache();
  return IN_PROCESS_REPORT_LIST_QUERY_PREFIXES.flatMap((prefix) =>
    queryCache.findAll({ queryKey: [...prefix] })
  );
}

/**
 * Updates a report in cache.reports (all cache entries that contain it).
 * Matches by report_uuid or workflow_run_uuid. Returns true if any entry was updated.
 *
 * Use from:
 * - WebSocket lock/unlock/lock_failed handlers
 * - 409 conflict handlers when opening a locked report
 * - Any code that must patch a single report in the reports cache
 */
export function updateReportInCache(
  cache: ApplicationCache,
  reportUuid: string | undefined,
  workflowRunUuid: string,
  updateFn: ReportUpdateFn
): boolean {
  if (!cache?.reports || !Array.isArray(cache.reports)) {
    return false;
  }

  let updated = false;
  const updatedReports = [...cache.reports];

  for (let i = 0; i < updatedReports.length; i++) {
    const entry = updatedReports[i];
    if (!entry?.items || Array.isArray(entry.items)) {
      continue;
    }
    const summary = entry.items as ReportSummaryApi;
    if (!summary.report_summaries || !Array.isArray(summary.report_summaries)) {
      continue;
    }

    const idx = summary.report_summaries.findIndex(
      (r) =>
        r.report_uuid === reportUuid || r.workflow_run_uuid === workflowRunUuid
    );
    if (idx === -1) {
      continue;
    }

    const updatedReport = updateFn(summary.report_summaries[idx]);
    const newSummaries = [...summary.report_summaries];
    newSummaries[idx] = updatedReport;
    updatedReports[i] = {
      ...entry,
      items: { ...summary, report_summaries: newSummaries },
      timestamp: Date.now(),
    };
    updated = true;
  }

  if (updated) {
    cache.reports = updatedReports;
  }
  return updated;
}

/**
 * Returns a copy of the summary with the report removed and total_rows decremented, or undefined if not found.
 */
function removeReportFromSummary(
  data: ReportSummaryApi,
  reportUuid: string | undefined,
  workflowRunUuid: string
): ReportSummaryApi | undefined {
  if (!data?.report_summaries) return undefined;
  const newSummaries = data.report_summaries.filter(
    (r) =>
      r.report_uuid !== reportUuid && r.workflow_run_uuid !== workflowRunUuid
  );
  if (newSummaries.length === data.report_summaries.length) return undefined;
  const removedCount = data.report_summaries.length - newSummaries.length;
  return {
    ...data,
    report_summaries: newSummaries,
    total_rows: Math.max(0, (data.total_rows ?? 0) - removedCount),
  };
}

/**
 * Removes a report from cache.reports (all entries that contain it).
 * Returns true if any entry was updated.
 */
export function removeReportFromCache(
  cache: ApplicationCache,
  reportUuid: string | undefined,
  workflowRunUuid: string
): boolean {
  if (!cache?.reports || !Array.isArray(cache.reports)) return false;
  let updated = false;
  const updatedReports = [...cache.reports];

  for (let i = 0; i < updatedReports.length; i++) {
    const entry = updatedReports[i];
    if (!entry?.items || Array.isArray(entry.items)) continue;
    const summary = entry.items as ReportSummaryApi;
    const next = removeReportFromSummary(summary, reportUuid, workflowRunUuid);
    if (next === undefined) continue;
    updatedReports[i] = { ...entry, items: next, timestamp: Date.now() };
    updated = true;
  }

  if (updated) cache.reports = updatedReports;
  return updated;
}

/**
 * Removes the report from every reportsInProcess query cache that contains it.
 * Returns true if at least one cache entry was updated.
 */
export function removeReportFromReportsInProcessQueryCache(
  queryClient: QueryClient,
  reportUuid: string | undefined,
  workflowRunUuid: string
): boolean {
  const queries = findInProcessReportListQueries(queryClient);
  let removed = false;
  queries.forEach((query) => {
    const data = query.state?.data as ReportSummaryApi | undefined;
    if (!data) return;
    const next = removeReportFromSummary(data, reportUuid, workflowRunUuid);
    if (next !== undefined) {
      queryClient.setQueryData(query.queryKey, next);
      removed = true;
    }
  });
  return removed;
}

/**
 * Removes the report from every reportInfoListQuery (completed list) cache that contains it.
 */
export function removeReportFromReportInfoListQueryCache(
  queryClient: QueryClient,
  reportUuid: string | undefined,
  workflowRunUuid: string
): void {
  const queryCache = queryClient.getQueryCache();
  const queries = queryCache.findAll({ queryKey: ["reportInfoListQuery"] });
  queries.forEach((query) => {
    const data = query.state?.data as ReportSummaryApi | undefined;
    if (!data) return;
    const next = removeReportFromSummary(data, reportUuid, workflowRunUuid);
    if (next !== undefined) {
      queryClient.setQueryData(query.queryKey, next);
    }
  });
}

/**
 * Applies the same report update to a ReportSummaryApi (e.g. React Query cached data).
 * Returns updated copy or undefined if report not found. Does not mutate input.
 */
export function applyUpdateToReportSummaryApi(
  data: ReportSummaryApi,
  reportUuid: string | undefined,
  workflowRunUuid: string,
  updateFn: ReportUpdateFn
): ReportSummaryApi | undefined {
  if (!data?.report_summaries) return undefined;
  const idx = data.report_summaries.findIndex(
    (r) =>
      r.report_uuid === reportUuid || r.workflow_run_uuid === workflowRunUuid
  );
  if (idx === -1) return undefined;
  const newSummaries = [...data.report_summaries];
  newSummaries[idx] = updateFn(newSummaries[idx]);
  return { ...data, report_summaries: newSummaries };
}

/**
 * Invalidates only reportsInProcess queries whose cached page's due_date range contains the given due_date.
 * List is ordered by due date; refetch only the page that could show this report.
 * If eventDueDate is undefined, no queries are invalidated (avoids full list reload).
 */
export function invalidateReportsInProcessQueriesContainingDueDate(
  queryClient: QueryClient,
  eventDueDate: string | undefined | null
): void {
  if (eventDueDate == null || eventDueDate === "") return;

  const queries = findInProcessReportListQueries(queryClient);

  queries.forEach((query) => {
    const data = query.state?.data as ReportSummaryApi | undefined;
    if (!data?.report_summaries?.length) return;

    const dueDates = data.report_summaries
      .map((r) => r.due_date)
      .filter((d): d is string => typeof d === "string" && d.length > 0);
    if (dueDates.length === 0) return;

    const minDue = dueDates.reduce((a, b) => (a <= b ? a : b));
    const maxDue = dueDates.reduce((a, b) => (a >= b ? a : b));
    if (eventDueDate >= minDue && eventDueDate <= maxDue) {
      queryClient.invalidateQueries({ queryKey: query.queryKey });
    }
  });
}

/**
 * Updates every reportsInProcess query cache entry that contains this report.
 * Uses React Query v3 QueryCache.findAll for partial key match.
 */
/**
 * @returns Number of React Query cache entries that were updated.
 */
export function updateReportsInProcessQueryCache(
  queryClient: QueryClient,
  reportUuid: string | undefined,
  workflowRunUuid: string,
  updateFn: ReportUpdateFn
): number {
  const queries = findInProcessReportListQueries(queryClient);
  let updatedCount = 0;
  queries.forEach((query) => {
    const data = query.state?.data as ReportSummaryApi | undefined;
    if (!data) return;
    const updated = applyUpdateToReportSummaryApi(
      data,
      reportUuid,
      workflowRunUuid,
      updateFn
    );
    if (updated) {
      queryClient.setQueryData(query.queryKey, updated);
      updatedCount += 1;
    }
  });
  return updatedCount;
}

/**
 * Patches the cached latest_step_run step name for a workflow run.
 *
 * v2026 in-process rows overlay step data from the ``latestWorkflowStepRun`` query (it wins
 * over the list row), so a ``workflow_status`` step change must also update this cache to render
 * in place — matching the legacy list behavior.
 *
 * Only updates entries that already exist (preserving ``payload``, which drives step permission
 * checks). When absent, the per-row query fetches fresh data including the new step.
 * @returns Number of cache entries updated.
 */
export function updateLatestWorkflowStepRunCache(
  queryClient: QueryClient,
  workflowRunUuid: string,
  stepName: string
): number {
  const queryCache = queryClient.getQueryCache();
  const queries = queryCache.findAll({
    queryKey: ["latestWorkflowStepRun", workflowRunUuid],
  });
  let updatedCount = 0;
  queries.forEach((query) => {
    const data = query.state?.data as LatestWorkflowStepRunApi | undefined;
    if (!data || data.step_name === stepName) return;
    queryClient.setQueryData(query.queryKey, {
      ...data,
      step_name: stepName,
    });
    updatedCount += 1;
  });
  return updatedCount;
}

export interface ApplyReportCacheUpdateOptions {
  /** Call after app cache is updated (e.g. persist to storage). Called with updated cache.reports. */
  onPersist?: () => void;
  /** Call after all updates (e.g. cacheUpdateEvents.notify). */
  onNotify?: (reportUuid: string | undefined) => void;
}

/**
 * Canonical "apply this report update everywhere": app cache and/or React Query reportsInProcess.
 * The in-process list is loaded via React Query; app cache is only populated by legacy paths.
 * We try both and succeed if either was updated (so lock/unlock works when list comes from React Query).
 *
 * - Updates cache.reports in place when present (legacy).
 * - Updates every reportsInProcess query cache that contains the report (primary path).
 * - Calls onPersist() only if app cache was updated; onNotify(reportUuid) if any cache was updated.
 */
export function applyReportCacheUpdate(
  cache: ApplicationCache,
  queryClient: QueryClient,
  reportUuid: string | undefined,
  workflowRunUuid: string,
  updateFn: ReportUpdateFn,
  options: ApplyReportCacheUpdateOptions = {}
): boolean {
  const cacheUpdated = updateReportInCache(
    cache,
    reportUuid,
    workflowRunUuid,
    updateFn
  );
  const queriesUpdated = updateReportsInProcessQueryCache(
    queryClient,
    reportUuid,
    workflowRunUuid,
    updateFn
  );
  const updated = cacheUpdated || queriesUpdated > 0;

  if (!updated) {
    return false;
  }

  if (cacheUpdated) options.onPersist?.();
  options.onNotify?.(reportUuid);
  return true;
}

/**
 * Removes a report from app cache and all reportsInProcess query caches (no refetch).
 * Use for workflow_completed and workflow_cancelled so the row disappears from the current page without reloading the list.
 * Always updates React Query caches (the visible list); app cache is legacy and may be empty.
 * Returns true if the report was removed from at least one cache.
 */
export function removeReportFromAllCaches(
  cache: ApplicationCache,
  queryClient: QueryClient,
  reportUuid: string | undefined,
  workflowRunUuid: string,
  options: ApplyReportCacheUpdateOptions = {}
): boolean {
  const removedFromAppCache = removeReportFromCache(
    cache,
    reportUuid,
    workflowRunUuid
  );

  const removedFromQueryCache = removeReportFromReportsInProcessQueryCache(
    queryClient,
    reportUuid,
    workflowRunUuid
  );

  const removed = removedFromAppCache || removedFromQueryCache;
  if (removedFromAppCache) options.onPersist?.();
  if (removed) options.onNotify?.(reportUuid);
  return removed;
}

/**
 * Removes a report from app cache, reportsInProcess, and reportInfoListQuery (completed list) caches.
 * Use for report_deleted so the report disappears from the completed list and any other cached lists.
 */
export function removeReportFromAllCachesIncludingCompleted(
  cache: ApplicationCache,
  queryClient: QueryClient,
  reportUuid: string | undefined,
  workflowRunUuid: string,
  options: ApplyReportCacheUpdateOptions = {}
): void {
  const removedFromAppCache = removeReportFromCache(
    cache,
    reportUuid,
    workflowRunUuid
  );
  removeReportFromReportsInProcessQueryCache(
    queryClient,
    reportUuid,
    workflowRunUuid
  );
  removeReportFromReportInfoListQueryCache(
    queryClient,
    reportUuid,
    workflowRunUuid
  );
  if (removedFromAppCache) options.onPersist?.();
  options.onNotify?.(reportUuid);
}
